import { sourceCoverageNotes } from './source-coverage';
import { recoveryWarnings, recoveryPending } from './recovery-warning';
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { UserRoles } from '@bandido/shared/types';
import { Link } from '@frontend/features/common/components/Link';
import { Table } from '@frontend/features/common/components/Table/Table';
import { routes } from '@frontend/router/routes';
import { ConversionMetrics } from './ConversionMetrics';
import { DayEndMetrics } from './DayEndMetrics';
import { ResponseMetrics } from './ResponseMetrics';
import { PerformancePrintReport } from './PerformancePrintReport';
import { MetricLabel, PerformanceHelp } from './PerformanceHelp';
import { PerformanceRequest } from './types';
import { localDateTime } from './model';
import { useCalendarRoster } from './calendar-queries';
import { performanceError, usePerformanceConfiguration, usePerformanceWorkload } from './queries';

export function PerformanceDashboard({ agencyId, agencyName, workflowId }: {
  agencyId: number; agencyName?: string; workflowId: string;
}) {
  const { myProfile } = useAuth();
  const { t } = useTranslation();
  const text = (key: string) => t(`workflowPerformance.${key}`);
  const [draft, setDraft] = useState(() => ({
    from: localDateTime(Date.now() - 86400000),
    to: localDateTime(Date.now()),
  }));
  const [view, setView] = useState<'workload' | 'conversion' | 'daily'>('workload');
  const [grain, setGrain] = useState<'advisor' | 'workflow'>('advisor');
  const [advisor, setAdvisor] = useState('');
  const [team, setTeam] = useState('');
  const [origin, setOrigin] = useState('');
  const [cutoff, setCutoff] = useState(() => localDateTime(Date.now()));
  const [responseClock, setResponseClock] = useState<'elapsed' | 'business'>('elapsed');
  const [request, setRequest] = useState<PerformanceRequest | null>(null);
  const [invalid, setInvalid] = useState(false);
  const allowed = Boolean(myProfile?.roles?.split(',').includes(UserRoles.ADMINS));
  const config = usePerformanceConfiguration(myProfile?.id, agencyId, workflowId, allowed);
  const enabled = allowed && !config.isError && Boolean(config.data?.agencyEnabled);
  const roster = useCalendarRoster(enabled ? myProfile?.id : undefined, agencyId);
  const query = usePerformanceWorkload(myProfile?.id, agencyId, workflowId, request, enabled);
  useEffect(() => {
    if (!enabled) setRequest(null);
  }, [enabled]);
  const changeRequest = (next: PerformanceRequest) => {
    setAdvisor(next.advisor ?? '');
    setTeam(next.team ? String(next.team) : '');
    setOrigin(next.origin ?? '');
    setRequest(next);
  };
  const clearResult = () => {
    setRequest(null);
    setInvalid(false);
  };
  const load = () => {
    const from = Date.parse(draft.from);
    const to = Date.parse(draft.to);
    if (
      !Number.isFinite(from) ||
      !Number.isFinite(to) ||
      to <= from ||
      to - from > 31 * 86400000 ||
      to > Date.now() + 5000 ||
      (view === 'conversion' &&
        (!Number.isFinite(Date.parse(cutoff)) || Date.parse(cutoff) < to || Date.parse(cutoff) > Date.now() + 5000))
    ) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    const next: PerformanceRequest = {
      from: new Date(from).toISOString(),
      to: new Date(to).toISOString(),
      ...(advisor && !(view === 'conversion' && grain === 'workflow') ? { advisor } : {}),
      ...(team ? { team: Number(team) } : {}),
      ...(view === 'conversion'
        ? { view, grain, cutoff: new Date(cutoff).toISOString(), ...(origin ? { origin } : {}) }
        : view === 'daily'
          ? { view }
          : {}),
    };
    if (JSON.stringify(request) === JSON.stringify(next)) void query.refetch();
    else setRequest(next);
  };
  const preset = (value: 'today' | 'week' | 'month') => {
    const now = Date.now(),
      start = new Date(now);
    start.setHours(0, 0, 0, 0);
    if (value === 'week') start.setDate(start.getDate() - 6);
    if (value === 'month') start.setDate(1);
    setDraft({ from: localDateTime(start.getTime()), to: localDateTime(now) });
    setCutoff(localDateTime(now));
    clearResult();
  };
  const report = request && enabled && !query.isError && !query.isFetching ? query.data : undefined;
  const advisors = new Map(roster.data?.advisors.map((row) => [row.id, row.name]) ?? []);
  const teams = new Map(roster.data?.teams.map((row) => [row.id, row.name]) ?? []);
  report?.advisors?.forEach((row) => {
    if (row.agentUserId) advisors.set(row.agentUserId, row.agentName ?? row.agentUserId);
    if (row.teamId) teams.set(String(row.teamId), row.teamName ?? String(row.teamId));
  });
  report?.conversion?.filters.forEach((row) => {
    if (row.advisor) advisors.set(row.advisor, row.advisorName ?? row.advisor);
    if (row.team) teams.set(String(row.team), row.teamName ?? String(row.team));
  });
  if (advisor && !advisors.has(advisor)) advisors.set(advisor, advisor);
  if (team && !teams.has(team)) teams.set(team, team);
  const rangeLabel = report && request
    ? [report.range?.from ?? request.from, report.range?.to ?? request.to]
      .map(value => new Date(value).toLocaleString()).join(' — ')
    : '';
  const none = text('unassigned');
  const label = (key: string, help: string) => <MetricLabel label={text(key)} help={text(`help.${help}`)} />;
  if (!allowed) return <Alert severity="info">{text('denied')}</Alert>;
  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap" spacing={1}>
        <Typography variant="h5">{config.data?.workflowName}</Typography>
        <Link
          to={`${routes.dashboard.agencyView.root.replace(':id', String(agencyId))}/workflows/${encodeURIComponent(workflowId)}/performance`}
        >
          {text('configure')}
        </Link>
      </Stack>
      {config.isError && (
        <Alert severity="error" action={<Button onClick={() => void config.refetch()}>{text('refresh')}</Button>}>
          {t(performanceError(config.error))}
        </Alert>
      )}
      {config.data && !config.isError && !config.data.agencyEnabled && (
        <Alert severity="info">{text('enableAgencyHint')}</Alert>
      )}
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
            spacing={1}
          >
            <Typography variant="h6">{text('dashboard.filters')}</Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button size="small" onClick={() => preset('today')}>
                {text('dayEnd.today')}
              </Button>
              <Button size="small" onClick={() => preset('week')}>
                {text('dashboard.lastWeek')}
              </Button>
              <Button size="small" onClick={() => preset('month')}>
                {text('dashboard.thisMonth')}
              </Button>
            </Stack>
          </Stack>
          <Box
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: 'repeat(4, 1fr)' }, gap: 2 }}
          >
            {(['from', 'to'] as const).map((field) => (
              <TextField
                key={field}
                type="datetime-local"
                label={text(field)}
                InputLabelProps={{ shrink: true }}
                value={draft[field]}
                onChange={(event) => {
                  setDraft({ ...draft, [field]: event.target.value });
                  clearResult();
                }}
              />
            ))}
            <TextField
              select
              label={text('workloadAdvisor')}
              value={advisor}
              disabled={!enabled || roster.isFetching || (view === 'conversion' && grain === 'workflow')}
              onChange={(event) => {
                setAdvisor(event.target.value);
                clearResult();
              }}
            >
              <MenuItem value="">{text('all')}</MenuItem>
              {[...advisors].map(([id, name]) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={text('workloadTeam')}
              value={team}
              disabled={!enabled || roster.isFetching}
              onChange={(event) => {
                setTeam(event.target.value);
                clearResult();
              }}
            >
              <MenuItem value="">{text('all')}</MenuItem>
              {[...teams].map(([id, name]) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {roster.isError && (
            <Alert severity="warning" action={<Button onClick={() => void roster.refetch()}>{text('refresh')}</Button>}>
              {text('dashboard.rosterError')}
            </Alert>
          )}
          {roster.data?.truncated && <Alert severity="warning">{text('dashboard.rosterLimited')}</Alert>}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
            spacing={2}
          >
            <Typography variant="body2" color="text.secondary">
              {t('workflowPerformance.timezone', { zone: Intl.DateTimeFormat().resolvedOptions().timeZone })}
            </Typography>
            {(advisor || team || origin) && (
              <Button
                onClick={() => {
                  setAdvisor('');
                  setTeam('');
                  setOrigin('');
                  clearResult();
                }}
              >
                {text('dashboard.clearFilters')}
              </Button>
            )}
          </Stack>
          {view === 'conversion' && origin && (
            <Chip
              sx={{ alignSelf: 'flex-start' }}
              label={`${text('origin')}: ${t(`cases.originatedByChannel.${origin}`, { defaultValue: origin })}`}
              onDelete={() => {
                setOrigin('');
                clearResult();
              }}
            />
          )}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" aria-label={text('dashboard.views')}>
            {(['workload', 'conversion', 'daily'] as const).map((value) => (
              <Stack key={value} direction="row" alignItems="center">
                <Button
                  variant={view === value ? 'contained' : 'outlined'}
                  aria-pressed={view === value}
                  onClick={() => {
                    setView(value);
                    clearResult();
                  }}
                >
                  {text(value)}
                </Button>
                <PerformanceHelp label={text(value)} help={text(`help.${value}`)} />
              </Stack>
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {text(`dashboard.${view}Intro`)}
          </Typography>
          {view === 'conversion' && (
            <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap">
              <Stack direction="row" spacing={1}>
                {(['advisor', 'workflow'] as const).map((value) => (
                  <Button
                    key={value}
                    variant={grain === value ? 'contained' : 'outlined'}
                    onClick={() => {
                      setGrain(value);
                      if (value === 'workflow') setAdvisor('');
                      clearResult();
                    }}
                  >
                    {text(value === 'advisor' ? 'conversionAdvisor' : 'conversionWorkflow')}
                  </Button>
                ))}
              </Stack>
              <TextField
                type="datetime-local"
                label={text('conversionCutoff')}
                InputLabelProps={{ shrink: true }}
                value={cutoff}
                helperText={text('dashboard.cutoffHint')}
                onChange={(event) => {
                  setCutoff(event.target.value);
                  clearResult();
                }}
              />
            </Stack>
          )}
          <Button
            onClick={load}
            variant="contained"
            sx={{ alignSelf: 'flex-start' }}
            disabled={!enabled || config.isFetching || query.isFetching}
          >
            {text('load')}
          </Button>
        </Stack>
      </Paper>
      {invalid && <Alert severity="error">{text(view === 'conversion' ? 'conversionRange' : 'range31')}</Alert>}
      {query.isFetching && <CircularProgress aria-label={text('loadingData')} />}
      {query.isError && <Alert severity="error">{t(performanceError(query.error))}</Alert>}
      {!request && enabled && !invalid && <Typography color="text.secondary">{text('dashboard.ready')}</Typography>}
      {report && request && (
        <>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
            <Chip
              label={rangeLabel}
            />
            {request.advisor && <Chip label={advisors.get(request.advisor) ?? request.advisor} />}
            {request.team && <Chip label={teams.get(String(request.team)) ?? request.team} />}
            {request.origin && (
              <Chip label={t(`cases.originatedByChannel.${request.origin}`, { defaultValue: request.origin })} />
            )}
            <Typography variant="body2" color="text.secondary">
              {t('workflowPerformance.generated', { at: new Date(report.generatedAt).toLocaleString() })}
            </Typography>
            <PerformancePrintReport report={report} request={request} agencyId={agencyId} agencyName={agencyName}
              workflowName={config.data?.workflowName ?? workflowId} responseClock={responseClock} />
          </Stack>
          <Alert severity="warning">{text('captureUnverified')}</Alert>
          <Alert severity="info">
            {sourceCoverageNotes(report.source, (key, values) => t(`workflowPerformance.${key}`, values)).map(note => <Typography key={note} variant="body2">{note}</Typography>)}
            <br />
            {t('workflowPerformance.sourceSince', {
              at: report.sourceSince ? new Date(report.sourceSince).toLocaleString() : '—',
            })}
          </Alert>
          {recoveryWarnings(report, (key, values) => t(`workflowPerformance.${key}`, values)).map((warning) => (
            <Alert severity="warning" key={warning}>{warning}</Alert>
          ))}
          {(report.capture.gapAt || report.capture.state !== 'observing') && (
            <Alert severity="warning">{text('captureGap')}</Alert>
          )}
          {recoveryPending(report) ? null : report.conversion ? (
            <ConversionMetrics report={report.conversion} request={request} onRequest={changeRequest} />
          ) : (
            <>
              {report.advisorsTruncated && <Alert severity="warning">{text('groupsTruncated')}</Alert>}
              {view === 'daily' && report.closings && (
                <DayEndMetrics report={report.closings} request={request} onRequest={changeRequest} />
              )}
              <Stack spacing={1}>
                <Typography variant="h6">{text('dashboard.receivedTitle')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {text('workloadHint')}
                </Typography>
                <Table
                  height={Math.min(400, Math.max(180, 90 + report.advisors.length * 55))}
                  columns={[
                    { id: 'advisor', label: text('workloadAdvisor') },
                    { id: 'team', label: text('workloadTeam') },
                    { id: 'origin', label: text('origin') },
                    { id: 'received', label: label('received', 'received') },
                  ]}
                  data={{
                    key: 'id',
                    totalCount: report.advisors.length,
                    items: report.advisors.map((row, index) => ({
                      ...row,
                      id: index,
                      advisor: row.agentUserId ? (
                        <Button
                          onClick={() =>
                            changeRequest({
                              ...request,
                              afterCaseId: 0,
                              afterResponseCaseId: undefined,
                              afterResponseId: undefined,
                              closingOffset: 0,
                              closingDate: undefined,
                              advisor: row.agentUserId!,
                            })
                          }
                        >
                          {row.agentName ?? row.agentUserId}
                        </Button>
                      ) : (
                        none
                      ),
                      team: row.teamId ? (
                        <Button
                          onClick={() =>
                            changeRequest({
                              ...request,
                              afterCaseId: 0,
                              afterResponseCaseId: undefined,
                              afterResponseId: undefined,
                              closingOffset: 0,
                              closingDate: undefined,
                              team: row.teamId!,
                            })
                          }
                        >
                          {row.teamName ?? row.teamId}
                        </Button>
                      ) : (
                        none
                      ),
                      origin: row.origin
                        ? t(`cases.originatedByChannel.${row.origin}`, { defaultValue: row.origin })
                        : none,
                    })),
                  }}
                />
              </Stack>
              {report.responses && (
                <ResponseMetrics
                  report={report.responses}
                  request={request}
                  onRequest={changeRequest}
                  clock={responseClock}
                  onClockChange={setResponseClock}
                />
              )}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">
                    {text('dashboard.currentTitle')}{' '}
                    <PerformanceHelp label={text('dashboard.currentTitle')} help={text('help.current')} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {text('dashboard.currentHint')}
                  </Typography>
                  <Table
                    height={Math.min(360, Math.max(180, 90 + report.advisors.length * 55))}
                    columns={[
                      { id: 'advisor', label: text('workloadAdvisor') },
                      { id: 'team', label: text('workloadTeam') },
                      { id: 'origin', label: text('origin') },
                      { id: 'portfolio', label: label('portfolio', 'current') },
                      { id: 'needsAgent', label: label('needsAgent', 'needsAgent') },
                    ]}
                    data={{
                      key: 'id',
                      totalCount: report.advisors.length,
                      items: report.advisors.map((row, id) => ({
                        ...row,
                        id,
                        advisor: row.agentName ?? row.agentUserId ?? none,
                        team: row.teamName ?? none,
                        origin: row.origin
                          ? t(`cases.originatedByChannel.${row.origin}`, { defaultValue: row.origin })
                          : none,
                      })),
                    }}
                  />
                  <Typography variant="subtitle1">{text('currentCases')}</Typography>
                  <Table
                    height={320}
                    columns={[
                      { id: 'case', label: text('case') },
                      { id: 'status', label: text('status') },
                      { id: 'pending', label: label('pendingSince', 'pendingSince') },
                      { id: 'observed', label: text('lastObserved') },
                    ]}
                    data={{
                      key: 'caseId',
                      totalCount: report.cases.length,
                      items: report.cases.map((row) => ({
                        ...row,
                        case: (
                          <Link to={routes.dashboard.caseView.replace(':id', String(row.caseId))}>#{row.caseId}</Link>
                        ),
                        status: t(`cases.status.${row.status}`, { defaultValue: row.status }),
                        pending: row.needsAgentSince ? new Date(row.needsAgentSince).toLocaleString() : '—',
                        observed: new Date(row.observedAt).toLocaleString(),
                      })),
                    }}
                  />
                  <Stack direction="row">
                    <Button
                      disabled={!request.afterCaseId}
                      onClick={() => changeRequest({ ...request, afterCaseId: 0 })}
                    >
                      {text('previous')}
                    </Button>
                    <Button
                      disabled={!report.nextCaseId}
                      onClick={() => changeRequest({ ...request, afterCaseId: report.nextCaseId! })}
                    >
                      {text('next')}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </>
          )}
        </>
      )}
    </Stack>
  );
}
